import React, { useState, useEffect } from 'react';
import {
  User,
  Settings,
  Key,
  Save,
  Trash2,
  Edit2,
  Check,
  X,
  Camera,
  Mail,
  Calendar,
  Activity,
  Shield,
  CreditCard,
  LogOut,
  Download,
  Upload,
  AlertCircle,
  Sparkles,
  Crown,
  Zap
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { avatarStorage } from '../services/firebase/storage';
import { promptsService, templatesService, historyService } from '../services/firebase/firestore';
import { subscriptionService, SUBSCRIPTION_BENEFITS } from '../services/subscription';
import UpgradeModal from './UpgradeModal';

interface UserProfileProps {
  onClose: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ onClose }) => {
  const { user, profile, updateProfile, updatePassword, updatePreferences, updateApiKeys, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'settings' | 'api' | 'subscription' | 'data'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Profile form state
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState({
    openai: profile?.apiKeys?.openai || '',
    anthropic: profile?.apiKeys?.anthropic || '',
    gemini: profile?.apiKeys?.gemini || ''
  });
  const [showApiKeys, setShowApiKeys] = useState({
    openai: false,
    anthropic: false,
    gemini: false
  });
  
  // Statistics
  const [stats, setStats] = useState({
    promptsCount: 0,
    templatesCount: 0,
    storageUsed: '0 MB'
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    loadUserStats();
  }, [user]);

  const loadUserStats = async () => {
    if (!user) return;
    
    try {
      const [prompts, templates] = await Promise.all([
        promptsService.getUserPrompts(user.uid, 1),
        templatesService.getUserTemplates(user.uid)
      ]);
      
      setStats({
        promptsCount: prompts.data.length,
        templatesCount: templates.data.length,
        storageUsed: '0 MB' // Calculate actual storage if needed
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      // Upload avatar if changed
      if (photoFile && user) {
        const photoURL = await avatarStorage.upload(photoFile, user.uid);
        await updateProfile({ displayName, photoURL });
      } else {
        await updateProfile({ displayName });
      }
      
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setIsEditing(false);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    
    setLoading(true);
    setMessage(null);
    
    try {
      await updatePassword(currentPassword, newPassword);
      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleApiKeysUpdate = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      await updateApiKeys(apiKeys);
      setMessage({ type: 'success', text: 'API keys updated successfully!' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const exportUserData = async () => {
    setLoading(true);
    try {
      const [prompts, templates, history] = await Promise.all([
        promptsService.getUserPrompts(user!.uid, 100),
        templatesService.getUserTemplates(user!.uid),
        historyService.getUserHistory(user!.uid, 100)
      ]);
      
      const data = {
        user: {
          email: user?.email,
          displayName: user?.displayName,
          createdAt: profile?.createdAt
        },
        prompts: prompts.data,
        templates: templates.data,
        history: history.data,
        exportedAt: new Date().toISOString()
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prompt-maker-data-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'Data exported successfully!' });
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to export data' });
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      // Delete user data from Firestore
      await historyService.clearUserHistory(user!.uid);
      // Note: In production, you'd also delete prompts, templates, etc.
      
      // Sign out and close modal
      await signOut();
      onClose();
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to delete account' });
      setLoading(false);
    }
  };

  if (!user || !profile) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-200 dark:border-slate-700">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b dark:border-slate-700 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Profile</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
          {['profile', 'settings', 'api', 'subscription', 'data'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`px-6 py-3 capitalize font-medium transition-all ${
                activeTab === tab
                  ? 'text-cyan-600 dark:text-cyan-400 border-b-2 border-cyan-500 bg-white dark:bg-slate-800'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-slate-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh] bg-white dark:bg-slate-800">
          {/* Message */}
          {message && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              message.type === 'success' 
                ? 'bg-green-100 text-green-700 border border-green-300'
                : 'bg-red-100 text-red-700 border border-red-300'
            }`}>
              {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              {message.text}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="flex items-start gap-6">
                {/* Avatar */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    {photoPreview || user.photoURL ? (
                      <img
                        src={photoPreview || user.photoURL}
                        alt="Avatar"
                        className="w-24 h-24 rounded-full object-cover border-4 border-cyan-200 dark:border-cyan-800"
                        onError={(e) => {
                          if (!photoPreview) {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              const fallback = document.createElement('div');
                              fallback.className = 'w-24 h-24 rounded-full border-4 border-cyan-200 dark:border-cyan-800 bg-gradient-to-br from-cyan-500 to-sky-500 flex items-center justify-center';
                              fallback.innerHTML = `<span class="text-white font-bold text-3xl">${(user.displayName || user.email || 'U')[0].toUpperCase()}</span>`;
                              parent.appendChild(fallback);
                            }
                          }
                        }}
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full border-4 border-cyan-200 dark:border-cyan-800 bg-gradient-to-br from-cyan-500 to-sky-500 flex items-center justify-center">
                        <span className="text-white font-bold text-3xl">
                          {(user.displayName || user.email || 'U')[0].toUpperCase()}
                        </span>
                      </div>
                    )}
                    {isEditing && (
                      <label className="absolute bottom-0 right-0 bg-gradient-to-r from-cyan-500 to-sky-500 text-white p-2 rounded-full cursor-pointer hover:from-cyan-600 hover:to-sky-600 transition-all shadow-lg">
                        <Camera className="w-4 h-4" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  <span className={`mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                    profile.subscription === 'pro' 
                      ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white shadow-md'
                      : profile.subscription === 'enterprise'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300'
                  }`}>
                    {profile.subscription?.toUpperCase() || 'FREE'}
                  </span>
                </div>

                {/* Profile Info */}
                <div className="flex-1 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Display Name
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
                      />
                    ) : (
                      <p className="text-gray-900 dark:text-white">{user.displayName || 'Not set'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email
                    </label>
                    <p className="text-gray-900 dark:text-white">{user.email}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Member Since
                    </label>
                    <p className="text-gray-900 dark:text-white">
                      {new Date(profile.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    {isEditing ? (
                      <>
                        <button
                          onClick={handleProfileUpdate}
                          disabled={loading}
                          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-sky-500 text-white rounded-lg hover:from-cyan-600 hover:to-sky-600 disabled:opacity-50 transition-all shadow-md hover:shadow-lg"
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setDisplayName(user.displayName || '');
                            setPhotoFile(null);
                            setPhotoPreview(null);
                          }}
                          className="px-4 py-2 border border-gray-300 dark:border-slate-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-all"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-sky-500 text-white rounded-lg hover:from-cyan-600 hover:to-sky-600 flex items-center gap-2 transition-all shadow-md hover:shadow-lg"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit Profile
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t dark:border-slate-700">
                <div className="text-center">
                  <Activity className="w-8 h-8 mx-auto text-sky-500 mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{profile.usage?.promptsGenerated || 0}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Prompts Generated</p>
                </div>
                <div className="text-center">
                  <Save className="w-8 h-8 mx-auto text-cyan-500 mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.promptsCount}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Saved Prompts</p>
                </div>
                <div className="text-center">
                  <Sparkles className="w-8 h-8 mx-auto text-teal-500 mb-2" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.templatesCount}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Templates</p>
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Preferences</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Dark Mode</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Use dark theme</p>
                    </div>
                    <button
                      onClick={() => updatePreferences({ theme: profile.preferences?.theme === 'dark' ? 'light' : 'dark' })}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        profile.preferences?.theme === 'dark' ? 'bg-gradient-to-r from-cyan-500 to-sky-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                        profile.preferences?.theme === 'dark' ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Auto-save</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Automatically save prompts</p>
                    </div>
                    <button
                      onClick={() => updatePreferences({ autoSave: !profile.preferences?.autoSave })}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        profile.preferences?.autoSave ? 'bg-gradient-to-r from-cyan-500 to-sky-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                        profile.preferences?.autoSave ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="border-t dark:border-slate-700 pt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Security</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
                    />
                  </div>
                  <button
                    onClick={handlePasswordUpdate}
                    disabled={loading || !currentPassword || !newPassword}
                    className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-sky-500 text-white rounded-lg hover:from-cyan-600 hover:to-sky-600 disabled:opacity-50 transition-all shadow-md hover:shadow-lg"
                  >
                    Update Password
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* API Keys Tab */}
          {activeTab === 'api' && (
            <div className="space-y-6">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900 dark:text-amber-300">Security Notice</p>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                      API keys are encrypted and stored securely. They are never exposed to other users.
                    </p>
                  </div>
                </div>
              </div>

              {/* OpenAI */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  OpenAI API Key
                </label>
                <div className="flex gap-2">
                  <input
                    type={showApiKeys.openai ? 'text' : 'password'}
                    value={apiKeys.openai}
                    onChange={(e) => setApiKeys({ ...apiKeys, openai: e.target.value })}
                    placeholder="sk-..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <button
                    onClick={() => setShowApiKeys({ ...showApiKeys, openai: !showApiKeys.openai })}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {showApiKeys.openai ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {/* Anthropic */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Anthropic (Claude) API Key
                </label>
                <div className="flex gap-2">
                  <input
                    type={showApiKeys.anthropic ? 'text' : 'password'}
                    value={apiKeys.anthropic}
                    onChange={(e) => setApiKeys({ ...apiKeys, anthropic: e.target.value })}
                    placeholder="sk-ant-..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <button
                    onClick={() => setShowApiKeys({ ...showApiKeys, anthropic: !showApiKeys.anthropic })}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {showApiKeys.anthropic ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {/* Google Gemini */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Google Gemini API Key
                </label>
                <div className="flex gap-2">
                  <input
                    type={showApiKeys.gemini ? 'text' : 'password'}
                    value={apiKeys.gemini}
                    onChange={(e) => setApiKeys({ ...apiKeys, gemini: e.target.value })}
                    placeholder="AIza..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <button
                    onClick={() => setShowApiKeys({ ...showApiKeys, gemini: !showApiKeys.gemini })}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {showApiKeys.gemini ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <button
                onClick={handleApiKeysUpdate}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Save API Keys
              </button>
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div className="space-y-6">
              {/* Current Plan */}
              <div className="text-center py-6 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-xl">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 ${
                  subscriptionService.isPro(profile)
                    ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white'
                    : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
                }`}>
                  {subscriptionService.isPro(profile) ? <Crown className="w-5 h-5" /> : <Zap className="w-5 h-5" />}
                  <span className="font-bold">
                    {subscriptionService.formatTierName(subscriptionService.getUserTier(profile))} Plan
                  </span>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  {subscriptionService.isPro(profile)
                    ? 'You have access to all premium features'
                    : 'Upgrade to Pro for unlimited features'}
                </p>
              </div>
              
              {/* Features List */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Current Plan Features */}
                <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-6">
                  <h4 className="font-semibold text-lg mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                    <span className="text-cyan-500">Current Features</span>
                  </h4>
                  <ul className="space-y-3">
                    {SUBSCRIPTION_BENEFITS[subscriptionService.getUserTier(profile) as keyof typeof SUBSCRIPTION_BENEFITS].slice(0, 6).map((benefit, index) => (
                      <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Upgrade Benefits (only show for free users) */}
                {!subscriptionService.isPro(profile) && (
                  <div className="border-2 border-amber-400 dark:border-amber-500 rounded-lg p-6 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
                    <h4 className="font-semibold text-lg mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                      <Crown className="w-5 h-5 text-amber-500" />
                      <span className="text-amber-600 dark:text-amber-400">Pro Benefits</span>
                    </h4>
                    <ul className="space-y-3 mb-6">
                      {SUBSCRIPTION_BENEFITS.pro.slice(0, 6).map((benefit, index) => (
                        <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                          {benefit}
                        </li>
                      ))}
                    </ul>
                    <button 
                      onClick={() => setShowUpgradeModal(true)}
                      className="w-full px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-md hover:shadow-lg font-semibold"
                    >
                      Upgrade to Pro - $9/month
                    </button>
                  </div>
                )}
              </div>
              
              {/* Usage Stats */}
              <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-6">
                <h4 className="font-semibold text-lg mb-4 text-gray-900 dark:text-white">Usage Statistics</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Prompts Generated</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{profile.usage?.promptsGenerated || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Prompts Saved</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {subscriptionService.isPro(profile) ? (profile.usage?.promptsSaved || 0) : '0 (Pro only)'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">API Calls</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{profile.usage?.apiCalls || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Data Tab */}
          {activeTab === 'data' && (
            <div className="space-y-6">
              <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-6 bg-white dark:bg-slate-900">
                <h3 className="font-semibold text-lg mb-4 text-gray-900 dark:text-white">Export Your Data</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Download all your prompts, templates, and settings in JSON format.
                </p>
                <button
                  onClick={exportUserData}
                  disabled={loading}
                  className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 disabled:opacity-50 flex items-center gap-2 transition-all shadow-md hover:shadow-lg"
                >
                  <Download className="w-4 h-4" />
                  Export All Data
                </button>
              </div>

              <div className="border border-red-200 dark:border-red-900 rounded-lg p-6 bg-red-50 dark:bg-red-950/20">
                <h3 className="font-semibold text-lg mb-4 text-red-900 dark:text-red-400">Danger Zone</h3>
                <p className="text-red-700 dark:text-red-400 mb-4">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <button
                  onClick={deleteAccount}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Account
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
          <button
            onClick={signOut}
            className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex items-center gap-2 transition-all"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 transition-all"
          >
            Close
          </button>
        </div>
      </div>
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="Save unlimited prompts and access premium features"
      />
    </div>
  );
};

export default UserProfile;